/**
 * Gamification System
 * 
 * Provides point tracking, achievements, and celebration features
 * to reinforce positive actions and reward CRM utilization.
 * 
 * Point Categories:
 * - Sales Activities: Opportunities, deals closed
 * - Engagement: Tasks, notes, customer interactions
 * - Quality: Feedback contributions, data completeness
 * - Consistency: Streaks, daily usage
 */

import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { generatePointsLogId } from './documentIdUtils';
import { isSalesTeamMember } from './salesTeamFilter';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  increment, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  deleteField
} from 'firebase/firestore';

// ============================================
// POINT VALUES - Weighted by impact
// ============================================
export const POINT_VALUES = {
  // Sales (High Impact)
  OPPORTUNITY_CREATED: 25,
  OPPORTUNITY_UPDATED: 5,
  OPPORTUNITY_STAGE_ADVANCED: 15,
  OPPORTUNITY_WON: 100,           // Base points, multiplied by deal tier
  OPPORTUNITY_WON_TIER_SMALL: 1,   // < $2,500
  OPPORTUNITY_WON_TIER_MEDIUM: 2,  // $2,500 - $5,000
  OPPORTUNITY_WON_TIER_LARGE: 3,   // $5,000 - $10,000
  OPPORTUNITY_WON_TIER_ENTERPRISE: 5, // > $10,000
  
  // Customer Engagement (Medium Impact)
  CUSTOMER_CREATED: 20,
  CUSTOMER_UPDATED: 3,
  CONTACT_CREATED: 15,
  CONTACT_UPDATED: 5,
  NOTE_CREATED: 10,
  
  // Task Management (Medium Impact)
  TASK_CREATED: 5,
  TASK_COMPLETED: 15,
  TASK_COMPLETED_ON_TIME: 25,     // Bonus for completing before due date
  TASK_OVERDUE_COMPLETED: 5,      // Reduced points for late completion
  
  // Quality Contributions (Medium Impact)
  FEEDBACK_SUBMITTED: 10,
  FEEDBACK_HELPFUL_SUBMITTED: 20, // Extra for positive feedback
  FAQ_FEEDBACK_SUBMITTED: 5,
  
  // Email Marketing (Medium Impact)
  CAMPAIGN_SENT: 30,
  
  // Consistency Bonuses
  DAILY_LOGIN: 5,
  WEEKLY_STREAK_BONUS: 50,        // 7 consecutive days
  MONTHLY_STREAK_BONUS: 200,      // 30 consecutive days
  
  // Data Quality
  CUSTOMER_PROFILE_COMPLETE: 10,  // All key fields filled
  OPPORTUNITY_WELL_DOCUMENTED: 10 // Notes + next steps defined
};

// ============================================
// ACHIEVEMENTS / BADGES
// ============================================
export const ACHIEVEMENTS = {
  // Sales Milestones
  FIRST_DEAL: {
    id: 'first_deal',
    name: 'First Win',
    description: 'Close your first deal',
    icon: '🏆',
    category: 'sales',
    points: 100,
    requirement: { type: 'deals_won', count: 1 }
  },
  DEAL_MAKER_10: {
    id: 'deal_maker_10',
    name: 'Deal Maker',
    description: 'Close 10 deals',
    icon: '💰',
    category: 'sales',
    points: 250,
    requirement: { type: 'deals_won', count: 10 }
  },
  DEAL_MASTER_50: {
    id: 'deal_master_50',
    name: 'Deal Master',
    description: 'Close 50 deals',
    icon: '👑',
    category: 'sales',
    points: 1000,
    requirement: { type: 'deals_won', count: 50 }
  },
  REVENUE_100K: {
    id: 'revenue_100k',
    name: 'Six Figure Club',
    description: 'Close $100,000 in revenue',
    icon: '💎',
    category: 'sales',
    points: 500,
    requirement: { type: 'revenue_closed', amount: 100000 }
  },
  REVENUE_500K: {
    id: 'revenue_500k',
    name: 'Half Million Hero',
    description: 'Close $500,000 in revenue',
    icon: '🌟',
    category: 'sales',
    points: 1500,
    requirement: { type: 'revenue_closed', amount: 500000 }
  },
  REVENUE_1M: {
    id: 'revenue_1m',
    name: 'Million Dollar Rep',
    description: 'Close $1,000,000 in revenue',
    icon: '🚀',
    category: 'sales',
    points: 5000,
    requirement: { type: 'revenue_closed', amount: 1000000 }
  },
  
  // Task Milestones
  TASK_STARTER: {
    id: 'task_starter',
    name: 'Task Starter',
    description: 'Complete 10 tasks',
    icon: '✅',
    category: 'tasks',
    points: 50,
    requirement: { type: 'tasks_completed', count: 10 }
  },
  TASK_CHAMPION: {
    id: 'task_champion',
    name: 'Task Champion',
    description: 'Complete 100 tasks',
    icon: '🏅',
    category: 'tasks',
    points: 300,
    requirement: { type: 'tasks_completed', count: 100 }
  },
  ON_TIME_HERO: {
    id: 'on_time_hero',
    name: 'On-Time Hero',
    description: 'Complete 25 tasks before their due date',
    icon: '⏰',
    category: 'tasks',
    points: 200,
    requirement: { type: 'tasks_on_time', count: 25 }
  },
  
  // Engagement Milestones
  NOTE_TAKER: {
    id: 'note_taker',
    name: 'Note Taker',
    description: 'Create 50 notes',
    icon: '📝',
    category: 'engagement',
    points: 100,
    requirement: { type: 'notes_created', count: 50 }
  },
  CUSTOMER_BUILDER: {
    id: 'customer_builder',
    name: 'Customer Builder',
    description: 'Add 25 new customers',
    icon: '🤝',
    category: 'engagement',
    points: 200,
    requirement: { type: 'customers_created', count: 25 }
  },
  
  // Consistency Milestones
  WEEK_WARRIOR: {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: 'Log in 7 consecutive days',
    icon: '🔥',
    category: 'consistency',
    points: 100,
    requirement: { type: 'login_streak', count: 7 }
  },
  MONTH_MASTER: {
    id: 'month_master',
    name: 'Month Master',
    description: 'Log in 30 consecutive days',
    icon: '⭐',
    category: 'consistency',
    points: 500,
    requirement: { type: 'login_streak', count: 30 }
  },
  
  // Quality Milestones
  FEEDBACK_FRIEND: {
    id: 'feedback_friend',
    name: 'Feedback Friend',
    description: 'Submit 10 helpful feedback responses',
    icon: '💬',
    category: 'quality',
    points: 100,
    requirement: { type: 'helpful_feedback', count: 10 }
  }
};

// ============================================
// LEVELS
// ============================================

// Default levels - used as fallback and for initial display before Firestore loads
export const DEFAULT_LEVELS = [
  { level: 1, name: 'Newcomer', minPoints: 0, icon: '🌱' },
  { level: 2, name: 'Contributor', minPoints: 2500, icon: '🌿' },
  { level: 3, name: 'Achiever', minPoints: 5000, icon: '🌳' },
  { level: 4, name: 'Expert', minPoints: 10000, icon: '⭐' },
  { level: 5, name: 'Champion', minPoints: 25000, icon: '🏆' },
  { level: 6, name: 'Master', minPoints: 50000, icon: '💎' },
  { level: 7, name: 'Legend', minPoints: 100000, icon: '👑' },
  { level: 8, name: 'Elite', minPoints: 250000, icon: '🚀' },
  { level: 9, name: 'Titan', minPoints: 500000, icon: '🔥' },
  { level: 10, name: 'Immortal', minPoints: 1000000, icon: '✨' }
];

// DEPRECATED: Use DEFAULT_LEVELS - kept for backwards compatibility
export const LEVELS = DEFAULT_LEVELS;

// Cache for merged levels (default + Firestore overrides)
let cachedLevels = null;
let levelsLoadPromise = null;

/**
 * Initialize/refresh levels cache from Firestore
 * Call this on app startup or when level config changes
 */
export async function initializeLevelsCache() {
  try {
    const docRef = doc(db, collectionPath('config'), 'levelOverrides');
    const snap = await getDoc(docRef);
    const overrides = snap.exists() ? snap.data() : {};
    
    cachedLevels = DEFAULT_LEVELS.map(level => ({
      ...level,
      ...(overrides[level.level] || {}),
      hasOverride: !!overrides[level.level]
    }));
    
    return cachedLevels;
  } catch (error) {
    console.error('Failed to load level overrides, using defaults:', error);
    cachedLevels = [...DEFAULT_LEVELS];
    return cachedLevels;
  }
}

/**
 * Get cached levels (with Firestore overrides applied)
 * Returns cached value synchronously, or triggers async load if not cached
 */
export function getCachedLevels() {
  if (cachedLevels) return cachedLevels;
  
  // Trigger async load if not already loading
  if (!levelsLoadPromise) {
    levelsLoadPromise = initializeLevelsCache().finally(() => {
      levelsLoadPromise = null;
    });
  }
  
  // Return defaults while loading
  return DEFAULT_LEVELS;
}

/**
 * Get user's current level based on points
 * Uses cached Firestore overrides for minPoints thresholds
 */
export function getUserLevel(totalPoints) {
  const levels = getCachedLevels();
  let currentLevel = levels[0];
  for (const level of levels) {
    if (totalPoints >= level.minPoints) {
      currentLevel = level;
    } else {
      break;
    }
  }
  return currentLevel;
}

/**
 * Get user's current level based on points (async version)
 * Ensures Firestore overrides are loaded before calculating
 */
export async function getUserLevelAsync(totalPoints) {
  // Ensure levels are loaded
  if (!cachedLevels) {
    await initializeLevelsCache();
  }
  return getUserLevel(totalPoints);
}

/**
 * Get next level and points needed
 */
export function getNextLevel(totalPoints) {
  const levels = getCachedLevels();
  const currentLevel = getUserLevel(totalPoints);
  const nextLevelIndex = levels.findIndex(l => l.level === currentLevel.level) + 1;
  
  if (nextLevelIndex >= levels.length) {
    return { nextLevel: null, pointsNeeded: 0, progress: 100 };
  }
  
  const nextLevel = levels[nextLevelIndex];
  const pointsNeeded = nextLevel.minPoints - totalPoints;
  const levelRange = nextLevel.minPoints - currentLevel.minPoints;
  const progress = Math.round(((totalPoints - currentLevel.minPoints) / levelRange) * 100);
  
  return { nextLevel, pointsNeeded, progress };
}

// ============================================
// FIRESTORE OPERATIONS
// ============================================

/**
 * Get current year
 */
export function getCurrentYear() {
  return new Date().getFullYear();
}

/**
 * Get or create user's gamification profile
 */
export async function getUserGamificationProfile(userId) {
  const profileRef = doc(db, collectionPath('userGamification'), userId);
  const profileSnap = await getDoc(profileRef);
  const currentYear = getCurrentYear();
  
  if (profileSnap.exists()) {
    const data = profileSnap.data();
    // Ensure yearlyPoints and achievementsByYear exist
    if (!data.yearlyPoints) data.yearlyPoints = {};
    if (!data.achievementsByYear) data.achievementsByYear = {};
    if (!data.statsByYear) data.statsByYear = {};
    return { id: profileSnap.id, ...data };
  }
  
  // Create new profile with yearly tracking
  const newProfile = {
    userId,
    totalPoints: 0,
    yearlyPoints: { [currentYear]: 0 },
    currentStreak: 0,
    longestStreak: 0,
    lastLoginDate: null,
    achievements: [],
    achievementsByYear: { [currentYear]: [] },
    stats: {
      dealsWon: 0,
      revenueClosed: 0,
      tasksCompleted: 0,
      tasksOnTime: 0,
      notesCreated: 0,
      customersCreated: 0,
      customersUpdated: 0,
      contactsUpdated: 0,
      opportunitiesCreated: 0,
      feedbackSubmitted: 0,
      helpfulFeedback: 0
    },
    statsByYear: {
      [currentYear]: {
        dealsWon: 0,
        revenueClosed: 0,
        tasksCompleted: 0,
        tasksOnTime: 0,
        notesCreated: 0,
        customersCreated: 0,
        customersUpdated: 0,
        contactsUpdated: 0,
        opportunitiesCreated: 0,
        feedbackSubmitted: 0,
        helpfulFeedback: 0
      }
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  await setDoc(profileRef, newProfile);
  return { id: userId, ...newProfile };
}

/**
 * Award points to a user with anti-abuse validation
 * @param {string} userId - User ID
 * @param {string} action - Action type (key from point values config)
 * @param {object} metadata - Additional context (e.g., deal value, source, entity data)
 * @returns {object} { pointsAwarded, newTotal, levelUp, newAchievements, blocked, blockReason }
 */
export async function awardPoints(userId, action, metadata = {}) {
  // Lazy import anti-abuse to avoid circular dependencies
  const { validatePointAction, recordPointAction } = await import('./gamificationAntiAbuse');
  
  // Anti-abuse validation
  const validation = await validatePointAction(userId, action, {
    entityId: metadata.entityId,
    entityData: metadata.entityData || {},
    oldData: metadata.oldData || null,
    source: metadata.source || 'manual',
    fieldsToCheck: metadata.fieldsToCheck || [],
    oldStage: metadata.oldStage,
    newStage: metadata.newStage
  });
  
  if (!validation.allowed) {
    return { 
      pointsAwarded: 0, 
      newTotal: 0, 
      blocked: true, 
      blockReason: validation.reason,
      blockMessage: validation.message,
      blockDetails: validation.details
    };
  }
  
  const profileRef = doc(db, collectionPath('userGamification'), userId);
  const currentYear = getCurrentYear();
  
  // Get current profile
  let profile = await getUserGamificationProfile(userId);
  
  // Load point values from Firestore config (with fallback to hard-coded)
  const pointConfig = await getPointValuesConfig();
  
  // Helper to get points for an action from config
  const getPoints = (actionKey) => {
    const val = pointConfig[actionKey];
    if (val === undefined) {
      return POINT_VALUES[actionKey] || 0;
    }
    // Check if action is enabled
    if (typeof val === 'object' && val.isEnabled === false) {
      return 0;
    }
    return typeof val === 'object' ? val.points : val;
  };
  
  // Calculate points
  let pointsAwarded = getPoints(action);
  
  // Apply multipliers for deal tiers
  if (action === 'OPPORTUNITY_WON' && metadata.dealValue) {
    const value = metadata.dealValue;
    let multiplier = getPoints('OPPORTUNITY_WON_TIER_SMALL');
    
    if (value >= 10000) {
      multiplier = getPoints('OPPORTUNITY_WON_TIER_ENTERPRISE');
    } else if (value >= 5000) {
      multiplier = getPoints('OPPORTUNITY_WON_TIER_LARGE');
    } else if (value >= 2500) {
      multiplier = getPoints('OPPORTUNITY_WON_TIER_MEDIUM');
    }
    
    pointsAwarded = getPoints('OPPORTUNITY_WON') * multiplier;
  }
  
  if (pointsAwarded === 0) {
    return { pointsAwarded: 0, newTotal: profile.totalPoints, blocked: false };
  }
  
  const oldTotal = profile.totalPoints;
  const newTotal = oldTotal + pointsAwarded;
  const oldLevel = getUserLevel(oldTotal);
  const newLevel = getUserLevel(newTotal);
  const levelUp = newLevel.level > oldLevel.level;
  
  // Record successful point action for cooldown/limit/entity tracking
  // Pass newStage as variant for stage advancement actions
  const stageVariant = action === 'OPPORTUNITY_STAGE_ADVANCED' ? metadata.newStage : null;
  await recordPointAction(userId, action, metadata.entityId, stageVariant);
  
  // Update stats based on action (both total and yearly)
  const statsUpdate = {};
  const yearlyStatsUpdate = {};
  
  switch (action) {
    case 'OPPORTUNITY_WON':
      statsUpdate['stats.dealsWon'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.dealsWon`] = increment(1);
      if (metadata.dealValue) {
        statsUpdate['stats.revenueClosed'] = increment(metadata.dealValue);
        yearlyStatsUpdate[`statsByYear.${currentYear}.revenueClosed`] = increment(metadata.dealValue);
      }
      break;
    case 'TASK_COMPLETED':
    case 'TASK_COMPLETED_ON_TIME':
    case 'TASK_OVERDUE_COMPLETED':
      statsUpdate['stats.tasksCompleted'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.tasksCompleted`] = increment(1);
      if (action === 'TASK_COMPLETED_ON_TIME') {
        statsUpdate['stats.tasksOnTime'] = increment(1);
        yearlyStatsUpdate[`statsByYear.${currentYear}.tasksOnTime`] = increment(1);
      }
      break;
    case 'NOTE_CREATED':
      statsUpdate['stats.notesCreated'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.notesCreated`] = increment(1);
      break;
    case 'CUSTOMER_CREATED':
      statsUpdate['stats.customersCreated'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.customersCreated`] = increment(1);
      break;
    case 'CUSTOMER_UPDATED':
      statsUpdate['stats.customersUpdated'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.customersUpdated`] = increment(1);
      break;
    case 'CONTACT_UPDATED':
      statsUpdate['stats.contactsUpdated'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.contactsUpdated`] = increment(1);
      break;
    case 'OPPORTUNITY_CREATED':
      statsUpdate['stats.opportunitiesCreated'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.opportunitiesCreated`] = increment(1);
      break;
    case 'FEEDBACK_SUBMITTED':
    case 'FEEDBACK_HELPFUL_SUBMITTED':
      statsUpdate['stats.feedbackSubmitted'] = increment(1);
      yearlyStatsUpdate[`statsByYear.${currentYear}.feedbackSubmitted`] = increment(1);
      if (action === 'FEEDBACK_HELPFUL_SUBMITTED') {
        statsUpdate['stats.helpfulFeedback'] = increment(1);
        yearlyStatsUpdate[`statsByYear.${currentYear}.helpfulFeedback`] = increment(1);
      }
      break;
    default:
      break;
  }
  
  // Update profile with both total and yearly points
  await updateDoc(profileRef, {
    totalPoints: increment(pointsAwarded),
    [`yearlyPoints.${currentYear}`]: increment(pointsAwarded),
    ...statsUpdate,
    ...yearlyStatsUpdate,
    updatedAt: serverTimestamp()
  });
  
  // Build updated stats for achievement checking (apply increments locally)
  const updatedStats = { ...(profile.stats || {}) };
  switch (action) {
    case 'OPPORTUNITY_WON':
      updatedStats.dealsWon = (updatedStats.dealsWon || 0) + 1;
      if (metadata.dealValue) {
        updatedStats.revenueClosed = (updatedStats.revenueClosed || 0) + metadata.dealValue;
      }
      break;
    case 'TASK_COMPLETED':
    case 'TASK_COMPLETED_ON_TIME':
    case 'TASK_OVERDUE_COMPLETED':
      updatedStats.tasksCompleted = (updatedStats.tasksCompleted || 0) + 1;
      if (action === 'TASK_COMPLETED_ON_TIME') {
        updatedStats.tasksOnTime = (updatedStats.tasksOnTime || 0) + 1;
      }
      break;
    case 'NOTE_CREATED':
      updatedStats.notesCreated = (updatedStats.notesCreated || 0) + 1;
      break;
    case 'CUSTOMER_CREATED':
      updatedStats.customersCreated = (updatedStats.customersCreated || 0) + 1;
      break;
    case 'CUSTOMER_UPDATED':
      updatedStats.customersUpdated = (updatedStats.customersUpdated || 0) + 1;
      break;
    case 'CONTACT_UPDATED':
      updatedStats.contactsUpdated = (updatedStats.contactsUpdated || 0) + 1;
      break;
    case 'OPPORTUNITY_CREATED':
      updatedStats.opportunitiesCreated = (updatedStats.opportunitiesCreated || 0) + 1;
      break;
    case 'FEEDBACK_SUBMITTED':
    case 'FEEDBACK_HELPFUL_SUBMITTED':
      updatedStats.feedbackSubmitted = (updatedStats.feedbackSubmitted || 0) + 1;
      if (action === 'FEEDBACK_HELPFUL_SUBMITTED') {
        updatedStats.helpfulFeedback = (updatedStats.helpfulFeedback || 0) + 1;
      }
      break;
    default:
      break;
  }
  
  // Check for new achievements with UPDATED stats
  const newAchievements = await checkAndAwardAchievements(userId, {
    ...updatedStats,
    currentStreak: profile.currentStreak || 0,
    totalPoints: newTotal
  });
  
  // Log the point award
  await logPointActivity(userId, action, pointsAwarded, metadata);
  
  return {
    pointsAwarded,
    newTotal,
    levelUp,
    oldLevel,
    newLevel,
    newAchievements
  };
}

/**
 * Check and award any new achievements
 */
async function checkAndAwardAchievements(userId, stats) {
  const profileRef = doc(db, collectionPath('userGamification'), userId);
  const profileSnap = await getDoc(profileRef);
  const profile = profileSnap.data();
  const earnedAchievements = profile.achievements || [];
  const newAchievements = [];
  
  for (const [key, achievement] of Object.entries(ACHIEVEMENTS)) {
    // Skip if already earned
    if (earnedAchievements.includes(achievement.id)) continue;
    
    // Check requirement
    let earned = false;
    const req = achievement.requirement;
    
    switch (req.type) {
      case 'deals_won':
        earned = (stats.dealsWon || 0) >= req.count;
        break;
      case 'revenue_closed':
        earned = (stats.revenueClosed || 0) >= req.amount;
        break;
      case 'tasks_completed':
        earned = (stats.tasksCompleted || 0) >= req.count;
        break;
      case 'tasks_on_time':
        earned = (stats.tasksOnTime || 0) >= req.count;
        break;
      case 'notes_created':
        earned = (stats.notesCreated || 0) >= req.count;
        break;
      case 'customers_created':
        earned = (stats.customersCreated || 0) >= req.count;
        break;
      case 'login_streak':
        earned = (stats.currentStreak || 0) >= req.count;
        break;
      case 'helpful_feedback':
        earned = (stats.helpfulFeedback || 0) >= req.count;
        break;
      default:
        break;
    }
    
    if (earned) {
      newAchievements.push(achievement);
      earnedAchievements.push(achievement.id);
    }
  }
  
  // Update profile with new achievements (both total and yearly)
  if (newAchievements.length > 0) {
    const currentYear = getCurrentYear();
    const bonusPoints = newAchievements.reduce((sum, a) => sum + a.points, 0);
    const newAchievementIds = newAchievements.map(a => a.id);
    
    // Get current yearly achievements
    const yearlyAchievements = profile.achievementsByYear?.[currentYear] || [];
    const updatedYearlyAchievements = [...new Set([...yearlyAchievements, ...newAchievementIds])];
    
    await updateDoc(profileRef, {
      achievements: earnedAchievements,
      [`achievementsByYear.${currentYear}`]: updatedYearlyAchievements,
      totalPoints: increment(bonusPoints),
      [`yearlyPoints.${currentYear}`]: increment(bonusPoints),
      updatedAt: serverTimestamp()
    });
  }
  
  return newAchievements;
}

/**
 * Log point activity for history
 * Uses standardized document ID format: points_{userId}_{action}_{timestamp}
 */
async function logPointActivity(userId, action, points, metadata) {
  const currentYear = getCurrentYear();
  const logId = generatePointsLogId(userId, action);
  const logRef = doc(db, collectionPath('pointsLog'), logId);
  await setDoc(logRef, {
    userId,
    action,
    points,
    year: currentYear,
    metadata,
    timestamp: serverTimestamp()
  });
}

/**
 * Update login streak
 * Uses Firestore config for point values
 */
export async function updateLoginStreak(userId) {
  const profileRef = doc(db, collectionPath('userGamification'), userId);
  const profile = await getUserGamificationProfile(userId);
  
  // Load point values from Firestore config
  const pointConfig = await getPointValuesConfig();
  const getPoints = (actionKey) => {
    const val = pointConfig[actionKey];
    if (val === undefined) return POINT_VALUES[actionKey] || 0;
    return typeof val === 'object' ? val.points : val;
  };
  
  const today = new Date().toISOString().split('T')[0];
  const lastLogin = profile.lastLoginDate;
  
  let newStreak = profile.currentStreak || 0;
  let streakBonus = 0;
  
  if (!lastLogin) {
    // First login
    newStreak = 1;
  } else {
    const lastDate = new Date(lastLogin);
    const todayDate = new Date(today);
    const diffDays = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      // Consecutive day
      newStreak = (profile.currentStreak || 0) + 1;
      
      // Check for streak bonuses
      if (newStreak === 7) {
        streakBonus = getPoints('WEEKLY_STREAK_BONUS');
      } else if (newStreak === 30) {
        streakBonus = getPoints('MONTHLY_STREAK_BONUS');
      }
    } else if (diffDays === 0) {
      // Same day, no change
      return { streak: newStreak, bonus: 0 };
    } else {
      // Streak broken
      newStreak = 1;
    }
  }
  
  const longestStreak = Math.max(profile.longestStreak || 0, newStreak);
  const dailyLoginPoints = getPoints('DAILY_LOGIN');
  
  await updateDoc(profileRef, {
    currentStreak: newStreak,
    longestStreak,
    lastLoginDate: today,
    totalPoints: increment(dailyLoginPoints + streakBonus),
    updatedAt: serverTimestamp()
  });
  
  // Log activity
  await logPointActivity(userId, 'DAILY_LOGIN', dailyLoginPoints, { streak: newStreak });
  if (streakBonus > 0) {
    await logPointActivity(userId, newStreak === 7 ? 'WEEKLY_STREAK_BONUS' : 'MONTHLY_STREAK_BONUS', streakBonus, { streak: newStreak });
  }
  
  return { streak: newStreak, bonus: dailyLoginPoints + streakBonus };
}

/**
 * Get leaderboard
 * @param {string} timeframe - 'all', 'year', 'month', 'week'
 * @param {number} limitCount - Max entries to return
 * @param {number} year - Year for yearly leaderboard (defaults to current year)
 *
 * Leaderboard results are scoped to users whose title marks them as members
 * of the sales team (see salesTeamFilter.SALES_TEAM_TITLES). Non-sales staff
 * still accumulate gamification points, but do not appear on displays.
 */
export async function getLeaderboard(timeframe = 'all', limitCount = 10, year = null) {
  const currentYear = year || getCurrentYear();

  // Build the set of user IDs eligible for the leaderboard (sales team only).
  // userGamification docs are keyed by uid and users docs are keyed by uid too,
  // so a simple Set of doc.id values from the users collection is sufficient.
  const usersSnap = await getDocs(collection(db, collectionPath('users')));
  const eligibleUserIds = new Set();
  usersSnap.docs.forEach((d) => {
    const data = d.data() || {};
    if (data.role === 'disabled') return;
    if (!isSalesTeamMember(data)) return;
    eligibleUserIds.add(d.id);
    if (data.uid && data.uid !== d.id) eligibleUserIds.add(data.uid);
  });

  // For yearly leaderboard, we need to sort by yearlyPoints[year]
  // Firestore doesn't support dynamic field ordering, so we fetch all and sort client-side
  if (timeframe === 'year') {
    const q = query(
      collection(db, collectionPath('userGamification')),
      limit(200) // Fetch more to ensure we get all users
    );
    
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by yearly points
    users.sort((a, b) => {
      const aPoints = a.yearlyPoints?.[currentYear] || 0;
      const bPoints = b.yearlyPoints?.[currentYear] || 0;
      return bPoints - aPoints;
    });
    
    // Filter to sales team members with >0 points for the year, then limit
    return users
      .filter(u => eligibleUserIds.has(u.id))
      .filter(u => (u.yearlyPoints?.[currentYear] || 0) > 0)
      .slice(0, limitCount)
      .map((user, index) => ({
        rank: index + 1,
        ...user
      }));
  }
  
  // Default: all-time leaderboard.
  // We can't push the sales-team filter into Firestore, so we over-fetch and
  // filter client-side to guarantee `limitCount` sales-team entries where possible.
  const q = query(
    collection(db, collectionPath('userGamification')),
    orderBy('totalPoints', 'desc'),
    limit(Math.max(limitCount * 4, 200))
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((entry) => eligibleUserIds.has(entry.id))
    .slice(0, limitCount)
    .map((entry, index) => ({ rank: index + 1, ...entry }));
}

/**
 * Subscribe to user's gamification profile for real-time updates
 */
export function subscribeToGamificationProfile(userId, callback) {
  const profileRef = doc(db, collectionPath('userGamification'), userId);
  return onSnapshot(profileRef, (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    }
  });
}

/**
 * Manager: Award bonus points to a user
 * @param {string} managerId - Manager's user ID
 * @param {string} targetUserId - Target user's ID
 * @param {number} points - Points to award
 * @param {string} reason - Reason for award
 * @param {number} year - Year to award points for (defaults to current year)
 */
export async function awardBonusPoints(managerId, targetUserId, points, reason, year = null) {
  const targetYear = year || getCurrentYear();
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  await updateDoc(profileRef, {
    totalPoints: increment(points),
    [`yearlyPoints.${targetYear}`]: increment(points),
    updatedAt: serverTimestamp()
  });
  
  // Log the bonus
  await logPointActivity(targetUserId, 'MANAGER_BONUS', points, {
    awardedBy: managerId,
    reason
  });
  
  return { success: true, pointsAwarded: points };
}

/**
 * Get user's recent point activity
 */
export async function getRecentActivity(userId, limitCount = 20) {
  const q = query(
    collection(db, collectionPath('pointsLog')),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// ============================================
// CELEBRATION TRIGGERS
// ============================================

/**
 * Determine if action warrants celebration
 */
export function shouldCelebrate(result) {
  // Level up always celebrates
  if (result.levelUp) return { celebrate: true, type: 'level_up', intensity: 'high' };
  
  // New achievements celebrate
  if (result.newAchievements?.length > 0) {
    return { celebrate: true, type: 'achievement', intensity: 'high' };
  }
  
  // Big point awards (50+) get medium celebration
  if (result.pointsAwarded >= 50) {
    return { celebrate: true, type: 'points', intensity: 'medium' };
  }
  
  // Smaller wins get subtle celebration
  if (result.pointsAwarded >= 15) {
    return { celebrate: true, type: 'points', intensity: 'low' };
  }
  
  return { celebrate: false };
}

/**
 * Format action for display
 */
export function formatActionName(action) {
  const names = {
    OPPORTUNITY_CREATED: 'Created Opportunity',
    OPPORTUNITY_UPDATED: 'Updated Opportunity',
    OPPORTUNITY_STAGE_ADVANCED: 'Advanced Deal Stage',
    OPPORTUNITY_WON: 'Closed Deal! 🎉',
    CUSTOMER_CREATED: 'Added Customer',
    CUSTOMER_UPDATED: 'Updated Customer',
    CONTACT_CREATED: 'Added Contact',
    CONTACT_UPDATED: 'Updated Contact',
    NOTE_CREATED: 'Logged Note',
    TASK_CREATED: 'Created Task',
    TASK_COMPLETED: 'Completed Task',
    TASK_COMPLETED_ON_TIME: 'Task Done Early! ⏰',
    TASK_OVERDUE_COMPLETED: 'Completed Overdue Task',
    FEEDBACK_SUBMITTED: 'Gave Feedback',
    FEEDBACK_HELPFUL_SUBMITTED: 'Helpful Feedback',
    DAILY_LOGIN: 'Daily Check-in',
    WEEKLY_STREAK_BONUS: 'Week Streak! 🔥',
    MONTHLY_STREAK_BONUS: 'Month Streak! 🌟',
    MANAGER_BONUS: 'Manager Reward',
    CAMPAIGN_SENT: 'Sent Campaign'
  };
  
  return names[action] || action.replace(/_/g, ' ').toLowerCase();
}
// ============================================
// ADMIN FUNCTIONS
// ============================================

/**
 * Get all gamification profiles (for admin)
 */
export async function getAllGamificationProfiles() {
  const q = query(
    collection(db, collectionPath('userGamification')),
    orderBy('totalPoints', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Admin: Manually adjust user points
 */
export async function adminAdjustPoints(adminId, targetUserId, points, reason, year = null) {
  const targetYear = year || getCurrentYear();
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Ensure profile exists
  await getUserGamificationProfile(targetUserId);
  
  await updateDoc(profileRef, {
    totalPoints: increment(points),
    [`yearlyPoints.${targetYear}`]: increment(points),
    updatedAt: serverTimestamp()
  });
  
  // Log the adjustment
  await logPointActivity(targetUserId, 'ADMIN_ADJUSTMENT', points, {
    adjustedBy: adminId,
    reason,
    year: targetYear
  });
  
  return { success: true, pointsAdjusted: points };
}

/**
 * Admin: Manually adjust user streak
 * @param {string} adminId - Admin user ID
 * @param {string} targetUserId - Target user ID
 * @param {number} streakChange - Change to streak (positive or negative)
 * @param {string} reason - Reason for adjustment
 */
export async function adminAdjustStreak(adminId, targetUserId, streakChange, reason) {
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Get current profile to update longest streak if needed
  const profile = await getUserGamificationProfile(targetUserId);
  const currentStreak = profile.currentStreak || 0;
  const newStreak = Math.max(0, currentStreak + streakChange); // Don't go below 0
  const longestStreak = Math.max(profile.longestStreak || 0, newStreak);
  
  await updateDoc(profileRef, {
    currentStreak: newStreak,
    longestStreak,
    updatedAt: serverTimestamp()
  });
  
  // Log the adjustment
  await logPointActivity(targetUserId, 'ADMIN_STREAK_ADJUSTMENT', 0, {
    adjustedBy: adminId,
    reason,
    oldStreak: currentStreak,
    newStreak,
    streakChange
  });
  
  return { success: true, oldStreak: currentStreak, newStreak };
}

/**
 * Admin: Manually adjust user lifetime points (totalPoints only, not yearly)
 * @param {string} adminId - Admin user ID
 * @param {string} targetUserId - Target user ID
 * @param {number} points - Change to lifetime points (positive or negative)
 * @param {string} reason - Reason for adjustment
 */
export async function adminAdjustLifetimePoints(adminId, targetUserId, points, reason) {
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Ensure profile exists
  await getUserGamificationProfile(targetUserId);
  
  await updateDoc(profileRef, {
    totalPoints: increment(points),
    updatedAt: serverTimestamp()
  });
  
  // Log the adjustment
  await logPointActivity(targetUserId, 'ADMIN_LIFETIME_ADJUSTMENT', points, {
    adjustedBy: adminId,
    reason,
    note: 'Lifetime points only (not yearly)'
  });
  
  return { success: true, pointsAdjusted: points };
}

/**
 * Admin: Grant achievement to user
 */
export async function adminGrantAchievement(adminId, targetUserId, achievementId, year = null) {
  const targetYear = year || getCurrentYear();
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Get current profile
  const profile = await getUserGamificationProfile(targetUserId);
  
  // Check if achievement exists
  const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
  if (!achievement) {
    throw new Error(`Achievement ${achievementId} not found`);
  }
  
  // Check if already earned
  if (profile.achievements?.includes(achievementId)) {
    throw new Error('User already has this achievement');
  }
  
  // Add achievement
  const updatedAchievements = [...(profile.achievements || []), achievementId];
  const yearlyAchievements = profile.achievementsByYear?.[targetYear] || [];
  const updatedYearlyAchievements = [...new Set([...yearlyAchievements, achievementId])];
  
  await updateDoc(profileRef, {
    achievements: updatedAchievements,
    [`achievementsByYear.${targetYear}`]: updatedYearlyAchievements,
    totalPoints: increment(achievement.points),
    [`yearlyPoints.${targetYear}`]: increment(achievement.points),
    updatedAt: serverTimestamp()
  });
  
  // Log the grant
  await logPointActivity(targetUserId, 'ADMIN_GRANT_ACHIEVEMENT', achievement.points, {
    grantedBy: adminId,
    achievementId,
    achievementName: achievement.name,
    year: targetYear
  });
  
  return { success: true, achievement };
}

/**
 * Admin: Revoke achievement from user
 */
export async function adminRevokeAchievement(adminId, targetUserId, achievementId) {
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Get current profile
  const profile = await getUserGamificationProfile(targetUserId);
  
  // Check if achievement exists and user has it
  const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
  if (!achievement) {
    throw new Error(`Achievement ${achievementId} not found`);
  }
  
  if (!profile.achievements?.includes(achievementId)) {
    throw new Error('User does not have this achievement');
  }
  
  // Remove achievement from all places
  const updatedAchievements = profile.achievements.filter(id => id !== achievementId);
  const updatedAchievementsByYear = {};
  
  if (profile.achievementsByYear) {
    for (const [yr, achievements] of Object.entries(profile.achievementsByYear)) {
      updatedAchievementsByYear[`achievementsByYear.${yr}`] = achievements.filter(id => id !== achievementId);
    }
  }
  
  await updateDoc(profileRef, {
    achievements: updatedAchievements,
    ...updatedAchievementsByYear,
    totalPoints: increment(-achievement.points),
    updatedAt: serverTimestamp()
  });
  
  // Log the revocation
  await logPointActivity(targetUserId, 'ADMIN_REVOKE_ACHIEVEMENT', -achievement.points, {
    revokedBy: adminId,
    achievementId,
    achievementName: achievement.name
  });
  
  return { success: true };
}

/**
 * Admin: Reset user's yearly data (for new year)
 * Also clears the main achievements array and stats to remove badge displays
 */
export async function adminResetYearlyData(adminId, targetUserId, year) {
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Get current profile to recalculate total points
  const profileSnap = await getDoc(profileRef);
  const profile = profileSnap.data() || {};
  const yearlyPoints = profile.yearlyPoints || {};
  const oldYearPoints = yearlyPoints[year] || 0;
  
  // Calculate new total points (subtract this year's points)
  const newTotalPoints = Math.max(0, (profile.totalPoints || 0) - oldYearPoints);
  
  await updateDoc(profileRef, {
    [`yearlyPoints.${year}`]: 0,
    [`achievementsByYear.${year}`]: [],
    achievements: [], // Clear the main achievements array too
    totalPoints: newTotalPoints,
    // Reset BOTH yearly stats AND main cumulative stats
    [`statsByYear.${year}`]: {
      dealsWon: 0,
      revenueClosed: 0,
      tasksCompleted: 0,
      tasksOnTime: 0,
      notesCreated: 0,
      customersCreated: 0,
      customersUpdated: 0,
      contactsUpdated: 0,
      opportunitiesCreated: 0,
      feedbackSubmitted: 0,
      helpfulFeedback: 0
    },
    // Also reset main stats object (cumulative stats)
    stats: {
      dealsWon: 0,
      revenueClosed: 0,
      tasksCompleted: 0,
      tasksOnTime: 0,
      notesCreated: 0,
      customersCreated: 0,
      customersUpdated: 0,
      contactsUpdated: 0,
      opportunitiesCreated: 0,
      feedbackSubmitted: 0,
      helpfulFeedback: 0
    },
    currentStreak: 0,
    longestStreak: 0, // Reset longest streak as well for full reset
    updatedAt: serverTimestamp()
  });
  
  // Log the reset
  await logPointActivity(targetUserId, 'ADMIN_YEARLY_RESET', -oldYearPoints, {
    resetBy: adminId,
    year,
    previousPoints: oldYearPoints
  });
  
  return { success: true, pointsCleared: oldYearPoints };
}

/**
 * Get point activity log for a user
 */
export async function getPointsLog(userId = null, limitCount = 100) {
  let q;
  if (userId) {
    q = query(
      collection(db, collectionPath('pointsLog')),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
  } else {
    q = query(
      collection(db, collectionPath('pointsLog')),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// ============================================
// ADMIN: TEST DATA GENERATION
// ============================================

/**
 * Generate test gamification data for a user
 * @param {string} adminId - Admin performing the action
 * @param {string} targetUserId - User to generate data for
 * @param {string} targetUserName - User's display name for logging
 */
export async function generateTestGamificationData(adminId, targetUserId, targetUserName = 'Test User') {
  const currentYear = getCurrentYear();
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Random test data
  const yearlyPoints = Math.floor(Math.random() * 3000) + 1000; // 1000-4000 points
  const totalPoints = yearlyPoints + Math.floor(Math.random() * 2000); // Add some historical points
  const currentStreak = Math.floor(Math.random() * 15) + 1; // 1-15 day streak
  const longestStreak = Math.max(currentStreak, Math.floor(Math.random() * 30) + 5);
  
  // Random stats
  const dealsWon = Math.floor(Math.random() * 20) + 3;
  const revenueClosed = dealsWon * (Math.floor(Math.random() * 15000) + 5000);
  const tasksCompleted = Math.floor(Math.random() * 80) + 20;
  const tasksOnTime = Math.floor(tasksCompleted * 0.7);
  const notesCreated = Math.floor(Math.random() * 40) + 10;
  const customersCreated = Math.floor(Math.random() * 15) + 5;
  const customersUpdated = Math.floor(Math.random() * 30) + 10;
  const contactsUpdated = Math.floor(Math.random() * 25) + 8;
  const opportunitiesCreated = Math.floor(Math.random() * 25) + 8;
  
  // Determine achievements based on stats
  const earnedAchievements = [];
  
  // Sales achievements
  if (dealsWon >= 1) earnedAchievements.push('first_deal');
  if (dealsWon >= 10) earnedAchievements.push('deal_maker_10');
  if (revenueClosed >= 100000) earnedAchievements.push('revenue_100k');
  
  // Task achievements
  if (tasksCompleted >= 10) earnedAchievements.push('task_starter');
  if (tasksOnTime >= 25) earnedAchievements.push('on_time_hero');
  
  // Engagement achievements
  if (notesCreated >= 50) earnedAchievements.push('note_taker');
  if (customersCreated >= 25) earnedAchievements.push('customer_builder');
  
  // Consistency achievements
  if (longestStreak >= 7) earnedAchievements.push('week_warrior');
  if (longestStreak >= 30) earnedAchievements.push('month_master');
  
  // Calculate points from achievements
  const achievementPoints = earnedAchievements.reduce((sum, achId) => {
    const ach = Object.values(ACHIEVEMENTS).find(a => a.id === achId);
    return sum + (ach?.points || 0);
  }, 0);
  
  const testData = {
    userId: targetUserId,
    totalPoints: totalPoints + achievementPoints,
    yearlyPoints: {
      [currentYear]: yearlyPoints + achievementPoints,
      [currentYear - 1]: Math.floor(Math.random() * 2000) + 500
    },
    currentStreak,
    longestStreak,
    lastLoginDate: new Date().toISOString().split('T')[0],
    achievements: earnedAchievements,
    achievementsByYear: {
      [currentYear]: earnedAchievements
    },
    stats: {
      dealsWon,
      revenueClosed,
      tasksCompleted,
      tasksOnTime,
      notesCreated,
      customersCreated,
      customersUpdated,
      contactsUpdated,
      opportunitiesCreated,
      feedbackSubmitted: Math.floor(Math.random() * 10),
      helpfulFeedback: Math.floor(Math.random() * 5)
    },
    statsByYear: {
      [currentYear]: {
        dealsWon,
        revenueClosed,
        tasksCompleted,
        tasksOnTime,
        notesCreated,
        customersCreated,
        customersUpdated,
        contactsUpdated,
        opportunitiesCreated,
        feedbackSubmitted: Math.floor(Math.random() * 10),
        helpfulFeedback: Math.floor(Math.random() * 5)
      }
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  // Use setDoc to create or overwrite the profile
  await setDoc(profileRef, testData);
  
  // Log the action
  await logPointActivity(targetUserId, 'ADMIN_GENERATED_TEST_DATA', testData.totalPoints, {
    generatedBy: adminId,
    userName: targetUserName,
    note: 'Test data generated for demonstration purposes'
  });
  
  return {
    success: true,
    data: {
      totalPoints: testData.totalPoints,
      yearlyPoints: testData.yearlyPoints[currentYear],
      achievements: earnedAchievements.length,
      streak: currentStreak
    }
  };
}

/**
 * Clear all gamification data for a user (for testing)
 */
export async function clearGamificationData(adminId, targetUserId) {
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  const emptyProfile = {
    userId: targetUserId,
    totalPoints: 0,
    yearlyPoints: {},
    currentStreak: 0,
    longestStreak: 0,
    lastLoginDate: null,
    achievements: [],
    achievementsByYear: {},
    stats: {},
    statsByYear: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  await setDoc(profileRef, emptyProfile);
  
  // Log the action
  await logPointActivity(targetUserId, 'ADMIN_CLEARED_DATA', 0, {
    clearedBy: adminId,
    note: 'All gamification data cleared'
  });
  
  return { success: true };
}

// ============================================
// CUSTOM ACHIEVEMENTS CRUD
// ============================================

/**
 * Get all custom achievements from Firestore
 */
export async function getCustomAchievements() {
  const q = query(
    collection(db, collectionPath('customAchievements')),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Create a new custom achievement
 */
export async function createCustomAchievement(adminId, achievementData) {
  const { name, description, icon, category, points, requirement } = achievementData;
  
  // Generate a slug-based ID
  const id = name.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 30);
  
  const docRef = doc(db, collectionPath('customAchievements'), id);
  
  // Check if ID already exists
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new Error('An achievement with a similar name already exists');
  }
  
  const data = {
    id,
    name,
    description,
    icon: icon || '🎯',
    category: category || 'custom',
    points: points || 100,
    requirement: requirement || { type: 'manual', count: 1 },
    isCustom: true,
    isEnabled: true,
    isArchived: false,
    createdBy: adminId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  await setDoc(docRef, data);
  
  return { success: true, id, data };
}

/**
 * Update a custom achievement
 */
export async function updateCustomAchievement(adminId, achievementId, updates) {
  const docRef = doc(db, collectionPath('customAchievements'), achievementId);
  
  const existing = await getDoc(docRef);
  if (!existing.exists()) {
    throw new Error('Achievement not found');
  }
  
  // Don't allow editing system achievements
  const data = existing.data();
  if (!data.isCustom) {
    throw new Error('Cannot edit system achievements');
  }
  
  // Filter allowed updates
  const allowedFields = ['name', 'description', 'icon', 'category', 'points', 'requirement', 'isEnabled', 'isArchived'];
  const filteredUpdates = {};
  for (const key of allowedFields) {
    if (updates[key] !== undefined) {
      filteredUpdates[key] = updates[key];
    }
  }
  
  filteredUpdates.updatedAt = serverTimestamp();
  filteredUpdates.updatedBy = adminId;
  
  await updateDoc(docRef, filteredUpdates);
  
  return { success: true };
}

/**
 * Delete a custom achievement
 */
export async function deleteCustomAchievement(adminId, achievementId) {
  const docRef = doc(db, collectionPath('customAchievements'), achievementId);
  
  const existing = await getDoc(docRef);
  if (!existing.exists()) {
    throw new Error('Achievement not found');
  }
  
  // Don't allow deleting system achievements
  const data = existing.data();
  if (!data.isCustom) {
    throw new Error('Cannot delete system achievements');
  }
  
  // Instead of hard delete, archive it
  await updateDoc(docRef, {
    isArchived: true,
    archivedAt: serverTimestamp(),
    archivedBy: adminId
  });
  
  return { success: true };
}

/**
 * Get all achievements (system + custom)
 */
export async function getAllAchievements() {
  const customAchievements = await getCustomAchievements();
  const overrides = await getSystemAchievementOverrides();
  
  // Merge system achievements with overrides
  const systemAchievements = Object.values(ACHIEVEMENTS).map(a => {
    const override = overrides[a.id];
    return {
      ...a,
      ...(override || {}),
      isCustom: false,
      isSystem: true,
      isArchived: false
    };
  });
  
  return [
    ...systemAchievements,
    ...customAchievements.filter(a => !a.isArchived)
  ];
}

// ============================================
// SYSTEM ACHIEVEMENT OVERRIDES
// ============================================

/**
 * Get system achievement overrides from Firestore
 */
export async function getSystemAchievementOverrides() {
  const docRef = doc(db, collectionPath('config'), 'achievementOverrides');
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : {};
}

/**
 * Update system achievement override (edit name, description, points, icon, enabled)
 */
export async function updateSystemAchievementOverride(adminId, achievementId, updates) {
  // Verify achievement exists in system
  const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
  if (!achievement) {
    throw new Error('System achievement not found');
  }
  
  const docRef = doc(db, collectionPath('config'), 'achievementOverrides');
  const snap = await getDoc(docRef);
  const currentOverrides = snap.exists() ? snap.data() : {};
  
  // Merge updates
  const allowedFields = ['name', 'description', 'icon', 'points', 'isEnabled'];
  const filteredUpdates = {};
  for (const key of allowedFields) {
    if (updates[key] !== undefined) {
      filteredUpdates[key] = updates[key];
    }
  }
  
  currentOverrides[achievementId] = {
    ...(currentOverrides[achievementId] || {}),
    ...filteredUpdates,
    updatedAt: new Date().toISOString(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, currentOverrides);
  return { success: true };
}

/**
 * Reset system achievement to default
 */
export async function resetSystemAchievementOverride(adminId, achievementId) {
  const docRef = doc(db, collectionPath('config'), 'achievementOverrides');
  const snap = await getDoc(docRef);
  
  if (!snap.exists()) return { success: true };
  
  const currentOverrides = snap.data();
  delete currentOverrides[achievementId];
  
  await setDoc(docRef, currentOverrides);
  return { success: true };
}

// ============================================
// UNARCHIVE CUSTOM ACHIEVEMENT
// ============================================

/**
 * Unarchive a custom achievement
 */
export async function unarchiveCustomAchievement(adminId, achievementId) {
  const docRef = doc(db, collectionPath('customAchievements'), achievementId);
  
  const existing = await getDoc(docRef);
  if (!existing.exists()) {
    throw new Error('Achievement not found');
  }
  
  await updateDoc(docRef, {
    isArchived: false,
    isEnabled: true,
    unarchivedAt: serverTimestamp(),
    unarchivedBy: adminId
  });
  
  return { success: true };
}

// ============================================
// LEVEL MANAGEMENT
// ============================================

/**
 * Get level overrides from Firestore
 */
export async function getLevelOverrides() {
  const docRef = doc(db, collectionPath('config'), 'levelOverrides');
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : {};
}

/**
 * Update a level's configuration
 */
export async function updateLevelOverride(adminId, levelNumber, updates) {
  // Verify level exists in system
  const level = DEFAULT_LEVELS.find(l => l.level === levelNumber);
  if (!level) {
    throw new Error('Level not found');
  }
  
  const docRef = doc(db, collectionPath('config'), 'levelOverrides');
  const snap = await getDoc(docRef);
  const currentOverrides = snap.exists() ? snap.data() : {};
  
  // Merge updates
  const allowedFields = ['name', 'icon', 'minPoints'];
  const filteredUpdates = {};
  for (const key of allowedFields) {
    if (updates[key] !== undefined) {
      filteredUpdates[key] = updates[key];
    }
  }
  
  currentOverrides[levelNumber] = {
    ...(currentOverrides[levelNumber] || {}),
    ...filteredUpdates,
    updatedAt: new Date().toISOString(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, currentOverrides);
  
  // Invalidate and refresh cache
  await initializeLevelsCache();
  
  return { success: true };
}

/**
 * Reset a level to default
 */
export async function resetLevelOverride(adminId, levelNumber) {
  const docRef = doc(db, collectionPath('config'), 'levelOverrides');
  const snap = await getDoc(docRef);
  
  if (!snap.exists()) return { success: true };
  
  const currentOverrides = snap.data();
  delete currentOverrides[levelNumber];
  
  await setDoc(docRef, currentOverrides);
  
  // Invalidate and refresh cache
  await initializeLevelsCache();
  
  return { success: true };
}

/**
 * Get merged levels (defaults + overrides)
 * Uses cached levels if available, otherwise fetches fresh
 */
export async function getMergedLevels() {
  // Ensure cache is populated
  if (!cachedLevels) {
    await initializeLevelsCache();
  }
  return cachedLevels || DEFAULT_LEVELS;
}

// ============================================
// POINT VALUES CONFIGURATION
// ============================================

/**
 * Default point values - used for initial seeding and fallback
 * Each includes: points, category, description, and triggerHint (how it's triggered)
 */
export const DEFAULT_POINT_VALUES = {
  // Sales (High Impact)
  OPPORTUNITY_CREATED: { points: 25, category: 'sales', description: 'Create a new opportunity', triggerHint: 'Triggered via OppModal when saving a new opportunity' },
  OPPORTUNITY_UPDATED: { points: 5, category: 'sales', description: 'Update an opportunity', triggerHint: 'Triggered via OppModal when updating existing opportunity' },
  OPPORTUNITY_STAGE_ADVANCED: { points: 15, category: 'sales', description: 'Advance opportunity to next stage', triggerHint: 'Triggered via OppModal when stage advances (not to Closed Lost)' },
  OPPORTUNITY_WON: { points: 100, category: 'sales', description: 'Win an opportunity (base, multiplied by tier)', triggerHint: 'Triggered via OppModal when stage changes to Closed Won' },
  OPPORTUNITY_WON_TIER_SMALL: { points: 1, category: 'sales', description: 'Tier multiplier: < $2,500', triggerHint: 'Multiplier applied automatically based on deal value' },
  OPPORTUNITY_WON_TIER_MEDIUM: { points: 2, category: 'sales', description: 'Tier multiplier: $2,500 - $5,000', triggerHint: 'Multiplier applied automatically based on deal value' },
  OPPORTUNITY_WON_TIER_LARGE: { points: 3, category: 'sales', description: 'Tier multiplier: $5,000 - $10,000', triggerHint: 'Multiplier applied automatically based on deal value' },
  OPPORTUNITY_WON_TIER_ENTERPRISE: { points: 5, category: 'sales', description: 'Tier multiplier: > $10,000', triggerHint: 'Multiplier applied automatically based on deal value' },
  
  // Customer Engagement (Medium Impact)
  CUSTOMER_CREATED: { points: 20, category: 'engagement', description: 'Add a new customer', triggerHint: 'Triggered via CustomerModal when saving a new customer' },
  CUSTOMER_UPDATED: { points: 3, category: 'engagement', description: 'Update customer information', triggerHint: 'Triggered via CustomerModal when updating existing customer' },
  CONTACT_CREATED: { points: 15, category: 'engagement', description: 'Add a new contact', triggerHint: 'Triggered via ContactModal when saving a new contact' },
  CONTACT_UPDATED: { points: 5, category: 'engagement', description: 'Update contact information', triggerHint: 'Triggered via ContactModal when updating existing contact' },
  NOTE_CREATED: { points: 10, category: 'engagement', description: 'Create a note', triggerHint: 'Triggered via NotesSection when adding a note' },
  
  // Task Management (Medium Impact)
  TASK_CREATED: { points: 5, category: 'tasks', description: 'Create a new task', triggerHint: 'Triggered via TaskModal when saving a new task' },
  TASK_COMPLETED: { points: 15, category: 'tasks', description: 'Complete a task', triggerHint: 'Triggered via task completion checkbox/button' },
  TASK_COMPLETED_ON_TIME: { points: 25, category: 'tasks', description: 'Complete task before due date', triggerHint: 'Triggered when task completed before dueDate' },
  TASK_OVERDUE_COMPLETED: { points: 5, category: 'tasks', description: 'Complete an overdue task', triggerHint: 'Triggered when task completed after dueDate' },
  
  // Quality Contributions (Medium Impact)
  FEEDBACK_SUBMITTED: { points: 10, category: 'quality', description: 'Submit feedback', triggerHint: 'Triggered via chatbot feedback submission' },
  FEEDBACK_HELPFUL_SUBMITTED: { points: 20, category: 'quality', description: 'Submit helpful/positive feedback', triggerHint: 'Triggered via chatbot positive feedback' },
  FAQ_FEEDBACK_SUBMITTED: { points: 5, category: 'quality', description: 'Submit FAQ feedback', triggerHint: 'Triggered via FAQ helpful/unhelpful buttons' },
  
  // Email Marketing (Medium Impact)
  CAMPAIGN_SENT: { points: 30, category: 'marketing', description: 'Send an email campaign', triggerHint: 'Triggered via SendCampaignModal when campaign is sent' },
  
  // Consistency Bonuses
  DAILY_LOGIN: { points: 5, category: 'consistency', description: 'Daily login bonus', triggerHint: 'Triggered automatically on first login each day' },
  WEEKLY_STREAK_BONUS: { points: 50, category: 'consistency', description: '7 consecutive login days', triggerHint: 'Triggered automatically on 7th consecutive login day' },
  MONTHLY_STREAK_BONUS: { points: 200, category: 'consistency', description: '30 consecutive login days', triggerHint: 'Triggered automatically on 30th consecutive login day' },
  
  // Data Quality
  CUSTOMER_PROFILE_COMPLETE: { points: 10, category: 'quality', description: 'Complete all key customer fields', triggerHint: 'Requires manual trigger or automation when all fields filled' },
  OPPORTUNITY_WELL_DOCUMENTED: { points: 10, category: 'quality', description: 'Add notes and next steps to opportunity', triggerHint: 'Requires manual trigger or automation when notes added' }
};

// Cache for point values
let pointValuesCache = null;
let pointValuesCacheTime = 0;
const POINT_VALUES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get point values from Firestore (with caching)
 */
export async function getPointValuesConfig() {
  const now = Date.now();
  if (pointValuesCache && (now - pointValuesCacheTime) < POINT_VALUES_CACHE_TTL) {
    return pointValuesCache;
  }
  
  const docRef = doc(db, collectionPath('config'), 'pointValues');
  const snap = await getDoc(docRef);
  
  if (snap.exists()) {
    pointValuesCache = snap.data();
    pointValuesCacheTime = now;
    return pointValuesCache;
  }
  
  // Seed with defaults if not exists
  await setDoc(docRef, {
    ...DEFAULT_POINT_VALUES,
    _seededAt: new Date().toISOString()
  });
  
  pointValuesCache = DEFAULT_POINT_VALUES;
  pointValuesCacheTime = now;
  return pointValuesCache;
}

/**
 * Get point value for a specific action (with fallback)
 */
export async function getPointValue(action) {
  const config = await getPointValuesConfig();
  const actionConfig = config[action];
  
  if (actionConfig) {
    return typeof actionConfig === 'object' ? actionConfig.points : actionConfig;
  }
  
  // Fallback to hard-coded POINT_VALUES for backwards compatibility
  return POINT_VALUES[action] || 0;
}

/**
 * Update a point value configuration
 */
export async function updatePointValue(adminId, action, updates) {
  const docRef = doc(db, collectionPath('config'), 'pointValues');
  const snap = await getDoc(docRef);
  const currentConfig = snap.exists() ? snap.data() : { ...DEFAULT_POINT_VALUES };
  
  const existingAction = currentConfig[action] || DEFAULT_POINT_VALUES[action] || {};
  
  currentConfig[action] = {
    ...(typeof existingAction === 'object' ? existingAction : { points: existingAction }),
    ...updates,
    updatedAt: new Date().toISOString(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, currentConfig);
  
  // Invalidate cache
  pointValuesCache = null;
  
  return { success: true };
}

/**
 * Reset a point value to default
 */
export async function resetPointValue(adminId, action) {
  const docRef = doc(db, collectionPath('config'), 'pointValues');
  const snap = await getDoc(docRef);
  
  if (!snap.exists()) return { success: true };
  
  const currentConfig = snap.data();
  
  // Reset to default
  if (DEFAULT_POINT_VALUES[action]) {
    currentConfig[action] = { ...DEFAULT_POINT_VALUES[action] };
  } else {
    delete currentConfig[action];
  }
  
  await setDoc(docRef, currentConfig);
  
  // Invalidate cache
  pointValuesCache = null;
  
  return { success: true };
}

/**
 * Get all point values with metadata
 */
export async function getAllPointValues() {
  const config = await getPointValuesConfig();
  
  // Merge DEFAULT_POINT_VALUES with stored config to ensure new actions appear
  // even if not yet in Firestore
  const mergedActions = new Set([
    ...Object.keys(DEFAULT_POINT_VALUES),
    ...Object.keys(config).filter(k => !k.startsWith('_'))
  ]);
  
  return Array.from(mergedActions)
    .map((action) => {
      const storedValue = config[action];
      const defaultVal = DEFAULT_POINT_VALUES[action];
      
      // Use stored value if exists, otherwise use default
      const value = storedValue || defaultVal;
      
      const isModified = storedValue && defaultVal && (
        (typeof storedValue === 'object' ? storedValue.points : storedValue) !== (typeof defaultVal === 'object' ? defaultVal.points : defaultVal)
      );
      const isCustom = !DEFAULT_POINT_VALUES[action]; // Custom if not in defaults
      
      return {
        action,
        points: typeof value === 'object' ? value.points : value,
        category: value?.category || defaultVal?.category || 'custom',
        description: value?.description || defaultVal?.description || action.replace(/_/g, ' ').toLowerCase(),
        isModified,
        isCustom,
        isEnabled: value?.isEnabled !== false, // Default to true
        triggerHint: value?.triggerHint || defaultVal?.triggerHint || null,
        defaultPoints: typeof defaultVal === 'object' ? defaultVal.points : defaultVal
      };
    })
    .sort((a, b) => {
      // Sort by category, then by action name
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      return a.action.localeCompare(b.action);
    });
}

/**
 * Create a new point value/scoring trigger
 * @param {string} adminId - Admin user creating this
 * @param {object} data - { action, points, category, description, triggerHint }
 */
export async function createPointValue(adminId, data) {
  const { action, points, category, description, triggerHint } = data;
  
  if (!action?.trim()) throw new Error('Action name is required');
  if (typeof points !== 'number' || points < 0) throw new Error('Points must be a positive number');
  
  // Sanitize action name to SCREAMING_SNAKE_CASE
  const sanitizedAction = action
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  
  if (!sanitizedAction) throw new Error('Invalid action name');
  
  const docRef = doc(db, collectionPath('config'), 'pointValues');
  const snap = await getDoc(docRef);
  const currentConfig = snap.exists() ? snap.data() : { ...DEFAULT_POINT_VALUES };
  
  // Check if already exists
  if (currentConfig[sanitizedAction]) {
    throw new Error(`Point value "${sanitizedAction}" already exists`);
  }
  
  // Add the new point value
  currentConfig[sanitizedAction] = {
    points: Math.round(points),
    category: category || 'custom',
    description: description?.trim() || `Award ${points} points for ${sanitizedAction.toLowerCase().replace(/_/g, ' ')}`,
    triggerHint: triggerHint?.trim() || null,
    isEnabled: true,
    isCustom: true,
    createdAt: new Date().toISOString(),
    createdBy: adminId
  };
  
  await setDoc(docRef, currentConfig);
  
  // Invalidate cache
  pointValuesCache = null;
  
  return { success: true, action: sanitizedAction };
}

/**
 * Delete a custom point value
 * Only custom (non-default) point values can be deleted
 */
export async function deletePointValue(adminId, action) {
  // Cannot delete default point values
  if (DEFAULT_POINT_VALUES[action]) {
    throw new Error('Cannot delete default point values. Use disable or reset instead.');
  }
  
  const docRef = doc(db, collectionPath('config'), 'pointValues');
  const snap = await getDoc(docRef);
  
  if (!snap.exists()) {
    throw new Error('Point values config not found');
  }
  
  const currentConfig = snap.data();
  
  if (!currentConfig[action]) {
    throw new Error(`Point value "${action}" not found`);
  }
  
  // Delete the field
  delete currentConfig[action];
  await setDoc(docRef, currentConfig);
  
  // Invalidate cache
  pointValuesCache = null;
  
  return { success: true };
}

/**
 * Enable or disable a point value
 */
export async function togglePointValueEnabled(adminId, action, isEnabled) {
  const docRef = doc(db, collectionPath('config'), 'pointValues');
  const snap = await getDoc(docRef);
  const currentConfig = snap.exists() ? snap.data() : { ...DEFAULT_POINT_VALUES };
  
  const existingAction = currentConfig[action] || DEFAULT_POINT_VALUES[action];
  if (!existingAction) {
    throw new Error(`Point value "${action}" not found`);
  }
  
  currentConfig[action] = {
    ...(typeof existingAction === 'object' ? existingAction : { points: existingAction }),
    isEnabled,
    updatedAt: new Date().toISOString(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, currentConfig);
  
  // Invalidate cache
  pointValuesCache = null;
  
  return { success: true };
}

// ============================================
// UNIFIED ACHIEVEMENTS (Firestore-based)
// ============================================

/**
 * Default achievements - used for initial seeding
 * These are the "system" achievements that previously were hard-coded
 */
export const DEFAULT_ACHIEVEMENTS = Object.values(ACHIEVEMENTS).map(a => ({
  ...a,
  isAutomatic: true, // Can be automatically awarded based on requirement
  isEnabled: true,
  createdAt: new Date().toISOString()
}));

// Cache for achievements
let achievementsCache = null;
let achievementsCacheTime = 0;
const ACHIEVEMENTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Seed achievements to Firestore if not already seeded
 * Call this once to migrate from hard-coded to Firestore
 */
export async function seedAchievementsToFirestore(adminId) {
  const configRef = doc(db, collectionPath('config'), 'achievementsSeeded');
  const configSnap = await getDoc(configRef);
  
  if (configSnap.exists()) {
    return { success: true, message: 'Achievements already seeded' };
  }
  
  const batch = writeBatch(db);
  
  // Seed each default achievement
  for (const achievement of DEFAULT_ACHIEVEMENTS) {
    const achRef = doc(db, collectionPath('achievements'), achievement.id);
    batch.set(achRef, {
      ...achievement,
      isEnabled: true,
      isAutomatic: true,
      seededFrom: 'system',
      seededAt: new Date().toISOString(),
      seededBy: adminId
    });
  }
  
  // Mark as seeded
  batch.set(configRef, {
    seededAt: new Date().toISOString(),
    seededBy: adminId,
    count: DEFAULT_ACHIEVEMENTS.length
  });
  
  await batch.commit();
  
  // Invalidate cache
  achievementsCache = null;
  
  return { success: true, message: `Seeded ${DEFAULT_ACHIEVEMENTS.length} achievements` };
}

/**
 * Get all achievements from Firestore (unified)
 * Falls back to hard-coded if not seeded yet
 */
export async function getAchievementsFromFirestore() {
  const now = Date.now();
  if (achievementsCache && (now - achievementsCacheTime) < ACHIEVEMENTS_CACHE_TTL) {
    return achievementsCache;
  }
  
  // Check if seeded
  const configRef = doc(db, collectionPath('config'), 'achievementsSeeded');
  const configSnap = await getDoc(configRef);
  
  if (!configSnap.exists()) {
    // Not seeded yet - return default achievements merged with any custom ones
    const customAchievements = await getCustomAchievements().catch(() => []);
    const overrides = await getSystemAchievementOverrides().catch(() => ({}));
    
    const result = [
      ...Object.values(ACHIEVEMENTS).map(a => ({
        ...a,
        ...(overrides[a.id] || {}),
        isAutomatic: true,
        isEnabled: overrides[a.id]?.isEnabled !== false
      })),
      ...customAchievements.filter(a => !a.isArchived)
    ];
    
    achievementsCache = result;
    achievementsCacheTime = now;
    return result;
  }
  
  // Load from Firestore
  const q = query(collection(db, collectionPath('achievements')));
  const snap = await getDocs(q);
  
  const achievements = snap.docs.map(d => ({
    id: d.id,
    ...d.data()
  }));
  
  achievementsCache = achievements;
  achievementsCacheTime = now;
  return achievements;
}

/**
 * Create a new achievement in Firestore
 */
export async function createAchievement(adminId, achievementData) {
  const { id, name, description, icon, points, category, requirement, isAutomatic } = achievementData;
  
  if (!name?.trim()) throw new Error('Achievement name is required');
  if (!description?.trim()) throw new Error('Achievement description is required');
  
  // Generate ID if not provided
  const achievementId = id || `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const achRef = doc(db, collectionPath('achievements'), achievementId);
  
  // Check if exists
  const existing = await getDoc(achRef);
  if (existing.exists()) {
    throw new Error('Achievement with this ID already exists');
  }
  
  await setDoc(achRef, {
    id: achievementId,
    name: name.trim(),
    description: description.trim(),
    icon: icon || '🏆',
    points: points || 0,
    category: category || 'custom',
    requirement: requirement || null,
    isAutomatic: isAutomatic || false,
    isEnabled: true,
    createdAt: new Date().toISOString(),
    createdBy: adminId
  });
  
  // Invalidate cache
  achievementsCache = null;
  
  return { success: true, id: achievementId };
}

/**
 * Update an achievement in Firestore
 */
export async function updateAchievement(adminId, achievementId, updates) {
  const achRef = doc(db, collectionPath('achievements'), achievementId);
  const snap = await getDoc(achRef);
  
  if (!snap.exists()) {
    // If not in unified collection, check if it's a system achievement
    const systemAch = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
    if (systemAch) {
      // Create it in Firestore with the update
      await setDoc(achRef, {
        ...systemAch,
        ...updates,
        isAutomatic: true,
        updatedAt: new Date().toISOString(),
        updatedBy: adminId
      });
      achievementsCache = null;
      return { success: true };
    }
    throw new Error('Achievement not found');
  }
  
  const allowedFields = ['name', 'description', 'icon', 'points', 'category', 'requirement', 'isAutomatic', 'isEnabled'];
  const filteredUpdates = {};
  for (const key of allowedFields) {
    if (updates[key] !== undefined) {
      filteredUpdates[key] = updates[key];
    }
  }
  
  filteredUpdates.updatedAt = new Date().toISOString();
  filteredUpdates.updatedBy = adminId;
  
  await updateDoc(achRef, filteredUpdates);
  
  // Invalidate cache
  achievementsCache = null;
  
  return { success: true };
}

/**
 * Delete an achievement from Firestore
 */
export async function deleteAchievement(adminId, achievementId) {
  const achRef = doc(db, collectionPath('achievements'), achievementId);
  const snap = await getDoc(achRef);
  
  if (!snap.exists()) {
    throw new Error('Achievement not found');
  }
  
  const data = snap.data();
  
  // Don't allow deleting seeded/automatic achievements, just disable them
  if (data.seededFrom === 'system' || data.isAutomatic) {
    await updateDoc(achRef, {
      isEnabled: false,
      disabledAt: new Date().toISOString(),
      disabledBy: adminId
    });
    achievementsCache = null;
    return { success: true, disabled: true };
  }
  
  // Actually delete custom achievements
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(achRef);
  
  // Invalidate cache
  achievementsCache = null;
  
  return { success: true, deleted: true };
}

/**
 * Invalidate achievements cache (call after external updates)
 */
export function invalidateAchievementsCache() {
  achievementsCache = null;
}

// ============================================
// GRANT MULTIPLE ACHIEVEMENTS
// ============================================

/**
 * Admin: Grant multiple achievements to user at once
 */
export async function adminGrantMultipleAchievements(adminId, targetUserId, achievementIds, year = null) {
  const targetYear = year || getCurrentYear();
  const profileRef = doc(db, collectionPath('userGamification'), targetUserId);
  
  // Get current profile
  const profile = await getUserGamificationProfile(targetUserId);
  
  // Get all achievements (system + custom)
  const allAchievements = await getAllAchievements();
  const achievementsMap = new Map(allAchievements.map(a => [a.id, a]));
  
  // Filter valid achievements
  const validAchievements = [];
  let totalPoints = 0;
  
  for (const achId of achievementIds) {
    const achievement = achievementsMap.get(achId);
    if (!achievement) continue;
    if (profile.achievements?.includes(achId)) continue; // Already has
    
    validAchievements.push(achievement);
    totalPoints += achievement.points || 0;
  }
  
  if (validAchievements.length === 0) {
    throw new Error('No new achievements to grant');
  }
  
  // Add achievements
  const updatedAchievements = [...(profile.achievements || []), ...validAchievements.map(a => a.id)];
  const yearlyAchievements = profile.achievementsByYear?.[targetYear] || [];
  const updatedYearlyAchievements = [...new Set([...yearlyAchievements, ...validAchievements.map(a => a.id)])];
  
  await updateDoc(profileRef, {
    achievements: updatedAchievements,
    [`achievementsByYear.${targetYear}`]: updatedYearlyAchievements,
    totalPoints: increment(totalPoints),
    [`yearlyPoints.${targetYear}`]: increment(totalPoints),
    updatedAt: serverTimestamp()
  });
  
  // Log each grant
  for (const achievement of validAchievements) {
    await logPointActivity(targetUserId, 'ADMIN_GRANT_ACHIEVEMENT', achievement.points || 0, {
      grantedBy: adminId,
      achievementId: achievement.id,
      achievementName: achievement.name,
      year: targetYear
    });
  }
  
  return { 
    success: true, 
    granted: validAchievements.length,
    totalPoints,
    achievements: validAchievements
  };
}

// ============================================
// ACTIVITY LOG MANAGEMENT
// ============================================

/**
 * Get activity log count
 */
export async function getActivityLogCount() {
  const q = query(collection(db, collectionPath('pointsLog')));
  const snap = await getDocs(q);
  return snap.size;
}

/**
 * Trim activity log - delete entries older than specified days
 * Uses a more robust approach that fetches entries and filters client-side
 * to avoid potential index issues with timestamp queries
 * Note: Comparison uses date only (start of day), ignoring time
 */
export async function trimActivityLog(adminId, daysToKeep = 90) {
  // Calculate cutoff at START of day (midnight) for date-only comparison
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
  cutoffDate.setHours(0, 0, 0, 0); // Set to start of day
  
  // Fetch all entries (up to 1000) - we'll filter client-side
  const q = query(
    collection(db, collectionPath('pointsLog')),
    orderBy('timestamp', 'asc'),
    limit(1000)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return { success: true, deleted: 0, message: 'No log entries found' };
  }
  
  // Filter entries older than cutoff date (comparing dates only, not time)
  const entriesToDelete = snapshot.docs.filter(docSnap => {
    const data = docSnap.data();
    if (!data.timestamp) return false; // Skip entries without timestamp
    
    // Handle both Firestore Timestamp and Date objects
    const entryDate = data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp);
    // Normalize entry date to start of day for fair comparison
    const entryDateOnly = new Date(entryDate);
    entryDateOnly.setHours(0, 0, 0, 0);
    
    return entryDateOnly < cutoffDate;
  });
  
  if (entriesToDelete.length === 0) {
    return { success: true, deleted: 0, message: `No entries older than ${daysToKeep} days found` };
  }
  
  // Delete in batches of 500 (Firestore limit)
  const batchSize = 500;
  let totalDeleted = 0;
  
  for (let i = 0; i < entriesToDelete.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = entriesToDelete.slice(i, i + batchSize);
    
    chunk.forEach(docSnap => {
      batch.delete(docSnap.ref);
    });
    
    await batch.commit();
    totalDeleted += chunk.length;
  }
  
  // Log the trim action
  await logPointActivity('SYSTEM', 'ADMIN_TRIMMED_LOG', 0, {
    trimmedBy: adminId,
    daysKept: daysToKeep,
    deletedCount: totalDeleted
  });
  
  return { 
    success: true, 
    deleted: totalDeleted,
    hasMore: snapshot.size === 1000 && entriesToDelete.length > 0
  };
}